import type { ReactNode } from 'react';

import Box from '@mui/material/Box';

import { brand } from '../theme';

export type StatusTone = 'success' | 'pending' | 'failed' | 'neutral';

const TONES: Record<StatusTone, { bg: string; fg: string }> = {
  success: { bg: brand.successBg, fg: brand.successFg },
  pending: { bg: brand.warnBg, fg: brand.warnFg },
  failed: { bg: brand.errorBg, fg: brand.errorFg },
  neutral: { bg: brand.canvasSunk, fg: brand.body },
};

/** Status pill used in activity rows — 11.5/700, 6px radius, tonal fill. */
export default function StatusChip({
  tone,
  children,
}: {
  tone: StatusTone;
  children: ReactNode;
}) {
  const { bg, fg } = TONES[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 22,
        px: 1.125,
        py: 0.25,
        borderRadius: '6px',
        backgroundColor: bg,
        color: fg,
        fontSize: 11.5,
        fontWeight: 700,
        lineHeight: 1.35,
        alignSelf: 'flex-start',
      }}
    >
      {children}
    </Box>
  );
}

/** Rounded-square activity glyph (↑ / ↓ / ⚑) tinted to match the row status. */
export function ActivityGlyph({ tone, symbol }: { tone: StatusTone; symbol: string }) {
  const { bg, fg } = tone === 'success' ? TONES.success : TONES[tone];
  const isPending = tone === 'pending';
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 36,
        height: 36,
        flex: 'none',
        borderRadius: '12px',
        backgroundColor: isPending ? brand.goldWash : bg,
        color: isPending ? brand.goldDeep : fg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontWeight: 800,
      }}
    >
      {symbol}
    </Box>
  );
}
